import { Link } from 'react-router-dom';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useNotificacoes } from '@/hooks/useNotificacoes';
import {
  getTipoConfig,
  getModuloBadgeColor,
} from '@/mocks/backoffice/notificacoes';

interface BackofficeHeaderProps {
  onToggleSidebar: () => void;
}

export default function BackofficeHeader({ onToggleSidebar }: BackofficeHeaderProps) {
  const { session } = useBackofficeAuth();
  const {
    notificacoes,
    naoLidas,
    totalNaoLidas,
    totalCriticos,
    marcarComoLida,
    marcarTodasComoLidas,
    notifOpen,
    abrirNotificacoes,
    fecharNotificacoes,
  } = useNotificacoes();

  const naoLidasOrdenadas = [...naoLidas].sort(
    (a, b) =>
      new Date(b.data).getTime() - new Date(a.data).getTime()
  );

  const lidasOrdenadas = notificacoes
    .filter((n) => n.lida)
    .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
    .slice(0, 5);

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
      {/* Left */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-300 cursor-pointer lg:hidden"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-menu-line text-gray-600" />
          </div>
        </button>
      </div>

      {/* Right */}
      <div className="flex items-center space-x-4">
        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => abrirNotificacoes()}
            className="relative p-2 hover:bg-gray-100 rounded-lg transition-all duration-300 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-notification-3-line text-gray-600" />
            </div>
            {totalNaoLidas > 0 && (
              <span
                className={`absolute -top-1 -right-1 w-5 h-5 text-white text-[10px] font-bold rounded-full flex items-center justify-center ${
                  totalCriticos > 0 ? 'bg-red-500' : 'bg-emerald-500'
                }`}
              >
                {totalNaoLidas > 9 ? '9+' : totalNaoLidas}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => fecharNotificacoes()}
              />
              <div className="absolute right-0 top-full mt-2 w-[360px] bg-white rounded-xl shadow-xl border border-gray-100 z-40 overflow-hidden"
              >
                {/* Header */}
                <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-bold text-gray-800">
                      Notificações
                    </h3>
                    {totalNaoLidas > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold">
                        {totalNaoLidas}
                      </span>
                    )}
                  </div>
                  {totalNaoLidas > 0 && (
                    <button
                      onClick={() => marcarTodasComoLidas()}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                    >
                      Marcar todas
                    </button>
                  )}
                </div>

                {/* Lista */}
                <div className="max-h-80 overflow-y-auto">
                  {/* Seção não lidas */}
                  {naoLidasOrdenadas.length > 0 && (
                    <div className="py-2">
                      <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                        Não lidas
                      </p>
                      {naoLidasOrdenadas.map((n) => {
                        const cfg = getTipoConfig(n.tipo);
                        return (
                          <div
                            key={n.id}
                            className="group relative"
                          >
                            <Link
                              to={n.link}
                              onClick={() => {
                                marcarComoLida(n.id);
                                fecharNotificacoes();
                              }}
                              className="flex items-start space-x-3 p-3 hover:bg-gray-50 transition-colors border-l-2 border-l-transparent hover:border-l-emerald-400"
                            >
                              <div
                                className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}
                              >
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i
                                    className={`${cfg.icon} ${cfg.color} text-sm`}
                                  />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-0.5">
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`}
                                  />
                                  <p className="text-xs font-bold text-gray-800 truncate">
                                    {n.titulo}
                                  </p>
                                </div>
                                <p className="text-[11px] text-gray-500 leading-relaxed">
                                  {n.descricao}
                                </p>
                                <div className="flex items-center space-x-2 mt-1.5">
                                  <span
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${getModuloBadgeColor(n.modulo)}`}
                                  >
                                    {n.modulo}
                                  </span>
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(n.data).toLocaleDateString(
                                      'pt-BR',
                                      {
                                        day: '2-digit',
                                        month: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      }
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Link>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                marcarComoLida(n.id);
                              }}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded cursor-pointer"
                              title="Marcar como lida"
                            >
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-check-double-line text-gray-400 text-xs" />
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Seção lidas */}
                  {lidasOrdenadas.length > 0 && (
                    <div className="py-2 border-t border-gray-50">
                      <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                        Já lidas
                      </p>
                      {lidasOrdenadas.map((n) => {
                        const cfg = getTipoConfig(n.tipo);
                        return (
                          <Link
                            key={n.id}
                            to={n.link}
                            onClick={() => fecharNotificacoes()}
                            className="flex items-start space-x-3 p-3 hover:bg-gray-50 transition-colors opacity-60"
                          >
                            <div
                              className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i
                                  className={`${cfg.icon} ${cfg.color} text-sm`}
                                />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-500 truncate">
                                {n.titulo}
                              </p>
                              <p className="text-[11px] text-gray-400 leading-relaxed">
                                {n.descricao}
                              </p>
                              <span className="text-[10px] text-gray-400">
                                {new Date(n.data).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  {notificacoes.length === 0 && (
                    <div className="p-6 text-center">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-notification-off-line text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-400">
                        Nenhuma notificação
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notificacoes.length > 0 && (
                  <div className="p-3 border-t border-gray-100 text-center">
                    <Link
                      to="/backoffice/auditoria"
                      onClick={() => fecharNotificacoes()}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                    >
                      Ver todas as ações no painel de auditoria
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* User */}
        <div className="flex items-center space-x-3">
          <div className="text-right hidden md:block">
            <p className="text-sm font-bold text-gray-800">
              {session.nome || 'Usuário'}
            </p>
            <p className="text-[10px] text-gray-500 capitalize">
              {session.role}
            </p>
          </div>
          <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
            <span className="text-sm font-bold text-white">
              {session.nome?.charAt(0) || 'U'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}